import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  LivingTrustPreferencesInfoRec,
  livingTrustPreferencesService,
} from '../../../../services/livingTrustPreferencesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('LivingTrustPreferencesData');

type LivingTrustPreferencesDataScreenRouteProp = RouteProp<RootStackParamList, 'LivingTrustPreferencesData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
  livingTrustPreferencesId?: number;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const LivingTrustPreferencesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<LivingTrustPreferencesDataScreenRouteProp>();
  const [livingTrustPreferencesInfo, setLivingTrustPreferencesInfo] = useState<LivingTrustPreferencesInfoRec | null>(null);
  const [loading, setLoading] = useState(true);

  const livingTrustPreferencesId = route.params?.livingTrustPreferencesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadLivingTrustPreferencesInfo();
  }, []);

  const loadLivingTrustPreferencesInfo = async () => {
    try {
      if (livingTrustPreferencesId) {
        const info = await livingTrustPreferencesService.getLivingTrustPreferencesById(livingTrustPreferencesId, targetUserId);
        setLivingTrustPreferencesInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await livingTrustPreferencesService.getAllLivingTrustPreferences(targetUserId);
        if (allInfo.length > 0) {
          setLivingTrustPreferencesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading living trust preferences info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (livingTrustPreferencesInfo) {
      // Map the data from livingTrustPreferencesInfo to the format expected by LivingTrustPreferencesInputScreen
      const mappedData = {
        id: livingTrustPreferencesInfo.id,
        hasLivingTrust: livingTrustPreferencesInfo.hasLivingTrust,
        hasLivingWill: livingTrustPreferencesInfo.hasLivingWill,
        trustLastUpdated: livingTrustPreferencesInfo.trustLastUpdated,
        trustLocation: livingTrustPreferencesInfo.trustLocation,
        trustExecutor: livingTrustPreferencesInfo.trustExecutor,
        trustExecutorEmail: livingTrustPreferencesInfo.trustExecutorEmail,
        trustExecutorNotified: livingTrustPreferencesInfo.trustExecutorNotified,
        trustAdditionalDetails: livingTrustPreferencesInfo.trustAdditionalDetails,
        willLastUpdated: livingTrustPreferencesInfo.willLastUpdated,
        willLocation: livingTrustPreferencesInfo.willLocation,
        willExecutor: livingTrustPreferencesInfo.willExecutor,
        willExecutorEmail: livingTrustPreferencesInfo.willExecutorEmail,
        willExecutorNotified: livingTrustPreferencesInfo.willExecutorNotified,
        willAdditionalDetails: livingTrustPreferencesInfo.willAdditionalDetails,
        notes: livingTrustPreferencesInfo.notes,
      };

      navigation.navigate('LivingTrustPreferencesInput', {
        livingTrustPreferencesData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('LivingTrustPreferencesInput');
    }
  };

  const handleDelete = () => {
    if (livingTrustPreferencesInfo) {
      Alert.alert(
        'Delete Living Trust Preferences',
        'Are you sure you want to delete these living trust preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await livingTrustPreferencesService.deleteLivingTrustPreferences(livingTrustPreferencesInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await livingTrustPreferencesService.getAllLivingTrustPreferences();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('living_trust_preferences');
                }

                (navigation as any).navigate('EstatePlanning', { screen: 'EstatePlanningChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting living trust preferences info:', error);
                Alert.alert('Error', 'Failed to delete living trust preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('EstatePlanning', { screen: 'EstatePlanningChecklist', params: { refreshKey: Date.now() } });
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Living Trust Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Living Trust Preferences Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {livingTrustPreferencesInfo ? (
            <>
              <InfoItem label="Has Living Trust" value={livingTrustPreferencesInfo.hasLivingTrust} />

              <InfoItem label="Has Living Will" value={livingTrustPreferencesInfo.hasLivingWill} />

              {livingTrustPreferencesInfo.trustLastUpdated && (
                <InfoItem label="Trust Last Updated" value={livingTrustPreferencesInfo.trustLastUpdated} />
              )}

              {livingTrustPreferencesInfo.trustLocation && (
                <InfoItem label="Trust Location" value={livingTrustPreferencesInfo.trustLocation} />
              )}

              {livingTrustPreferencesInfo.trustExecutor && (
                <InfoItem label="Trust Executor" value={livingTrustPreferencesInfo.trustExecutor} />
              )}

              {livingTrustPreferencesInfo.trustExecutorEmail && (
                <InfoItem label="Trust Executor Email" value={livingTrustPreferencesInfo.trustExecutorEmail} />
              )}

              {livingTrustPreferencesInfo.trustExecutorNotified && (
                <InfoItem label="Trust Executor Notified" value={livingTrustPreferencesInfo.trustExecutorNotified} />
              )}

              {livingTrustPreferencesInfo.trustAdditionalDetails && (
                <InfoItem label="Additional Trust Details" value={livingTrustPreferencesInfo.trustAdditionalDetails} />
              )}

              {livingTrustPreferencesInfo.willLastUpdated && (
                <InfoItem label="Will Last Updated" value={livingTrustPreferencesInfo.willLastUpdated} />
              )}

              {livingTrustPreferencesInfo.willLocation && (
                <InfoItem label="Will Location" value={livingTrustPreferencesInfo.willLocation} />
              )}

              {livingTrustPreferencesInfo.willExecutor && (
                <InfoItem label="Will Executor" value={livingTrustPreferencesInfo.willExecutor} />
              )}

              {livingTrustPreferencesInfo.willExecutorEmail && (
                <InfoItem label="Will Executor Email" value={livingTrustPreferencesInfo.willExecutorEmail} />
              )}

              {livingTrustPreferencesInfo.willExecutorNotified && (
                <InfoItem label="Will Executor Notified" value={livingTrustPreferencesInfo.willExecutorNotified} />
              )}

              {livingTrustPreferencesInfo.willAdditionalDetails && (
                <InfoItem label="Additional Will Details" value={livingTrustPreferencesInfo.willAdditionalDetails} />
              )}

              {livingTrustPreferencesInfo.notes && (
                <InfoItem label="Additional Notes" value={livingTrustPreferencesInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No living trust preferences found.</Text>
              <TouchableOpacity
                style={[styles.button, styles.editButton, { marginTop: 20 }]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Add Living Trust Preferences</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0', // Teal header background
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF', // White title on teal background
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // White content area
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280', // Gray label text
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F', // Dark blue value text
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0', // Teal separator line
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0', // Teal back button
  },
  deleteButton: {
    backgroundColor: '#FF4444', // Red delete button
  },
  editButton: {
    backgroundColor: '#FF9F5A', // Orange edit button
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});